import URI from 'jsuri';
import Q from 'q';

import API from 'js/lib/api';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import userInfo from 'js/lib/user';

import PeerReviewAPIUtils from 'bundles/author-peer-common/utils/PeerReviewAPIUtils';
import type { ProctorConfigurationDraft } from 'bundles/authoring/common/types/proctorConfig';

type ProctorConfigurationMetadata = {
  createdBy?: number | null;
  createdAt: number;
  revertTo?: string | null;
  publishedTo?: string | null;
};

const PROCTOR_FIELDS = ['draft', 'metadata'];
const ProctorAPI = API('/api/authoringProctorConfigurationDrafts.v2', {
  type: 'rest',
});

// [be-tech-debt] PARTNER-16877: FE is manually constructing the authoringItemContentId
// Best solution would be for CourseMaterials  to construct the authoringItemContentId
// and make it generally available in addition to the usual branchId, itemId
export const getAuthoringContentId = (branchId: string, itemId: string) => {
  return tupleToStringKey([branchId, itemId]);
};

function buildBaseURI(base?: string) {
  return new URI(base).addQueryParam('fields', PROCTOR_FIELDS.join(','));
}

export function getProctorConfigDraft(draftId: string, branchId: string, itemId: string) {
  const authoringItemContentId = getAuthoringContentId(branchId, itemId);

  const uri = buildBaseURI()
    .addQueryParam('q', 'byProctorConfigurationIdAndAuthoringItemContentId')
    .addQueryParam('id', draftId)
    .addQueryParam('authoringItemContentId', authoringItemContentId);

  return Q(ProctorAPI.get(uri.toString())).then((response) => {
    return response.elements[0];
  });
}

export function getSpecificProctorConfigDraft(
  standardProctorConfigurationId: string,
  submitterId: string,
  branchId: string,
  itemId: string
) {
  const authoringItemContentId = getAuthoringContentId(branchId, itemId);

  const uri = new URI()
    .addQueryParam('action', 'getOrBuildSpecificDraft')
    .addQueryParam('id', standardProctorConfigurationId)
    .addQueryParam('authoringItemContentId', authoringItemContentId)
    .addQueryParam('submitterId', `user~${submitterId}`);

  const data = {
    createdAt: Date.now(),
    createdBy: userInfo.get().id,
  };

  return Q<ProctorConfigurationDraft>(ProctorAPI.post(uri.toString(), { data })).then((response) => response);
}

/* Temporary API util to get proctor config for Peer Review
  See https://coursera.slack.com/archives/C02UVDJS880/p1650395379697829?thread_ts=1650388218.119629&cid=C02UVDJS880
  We are structuring the response in a way that is similar to the current structure for Quizzes and SGAs */
export function getPeerReviewProctorConfigDraft(branchId: string, itemId: string, atomId: string) {
  return PeerReviewAPIUtils.get(branchId, itemId, atomId).then((response) => {
    const maxAttempts = response?.elements?.[0]?.published?.attemptConfiguration?.maxAttempts;
    // max attempts for a learner can be overriden by a grader to 0. This is also possbile for SGAs
    // max attempts = 0 means there can be no attempts made
    if (maxAttempts >= 0) {
      return {
        maxAttempts,
        createdAt: 0,
        attemptConfigurations: [
          {
            maxSubmissions: 1,
            timeLimit: {
              duration: null,
              gracePeriod: null,
              lateSubmissionPolicy: {
                'org.coursera.proctor.configuration.MustSubmitLastSavedVersionAtTimeLimit': {},
              },
            },
          },
        ],
      };
    }

    return undefined;
  });
}

export function createProctorConfigDraft(branchId: string, itemId: string) {
  const authoringItemContentId = getAuthoringContentId(branchId, itemId);
  const uri = buildBaseURI();
  const data = {
    typeName: 'standardCreate',
    definition: {
      authoringItemContentId,
    },
  };

  return Q(ProctorAPI.post(uri.toString(), { data })).then((response) => {
    return response.elements[0];
  });
}

export function updateProctorConfigDraft(id: string, draft: $TSFixMe, metadata: ProctorConfigurationMetadata) {
  const uri = buildBaseURI(id);
  const data = {
    id,
    draft,
    metadata,
  };

  return Q(ProctorAPI.put(uri.toString(), { data })).then((response) => {
    return response.elements[0];
  });
}

export function deleteProctorConfigDraft(id: string) {
  const uri = buildBaseURI(id);

  return Q(ProctorAPI.delete(uri.toString())).then((response) => {
    return response.elements[0];
  });
}

export function cloneProctorConfigDraft(publishedId: string, branchId: string, itemId: string) {
  const authoringItemContentId = getAuthoringContentId(branchId, itemId);

  const uri = buildBaseURI()
    .addQueryParam('action', 'getOrCreateFromPublished')
    .addQueryParam('id', publishedId)
    .addQueryParam('authoringItemContentId', authoringItemContentId);

  return Q(ProctorAPI.post(uri.toString())).then((response) => {
    return response;
  });
}

export function revertProctorConfigDraft(id: string, metadata: ProctorConfigurationMetadata) {
  const uri = buildBaseURI().addQueryParam('action', 'revert').addQueryParam('id', id);
  const data = metadata;

  return Q(ProctorAPI.post(uri.toString(), { data })).then((response) => {
    return response;
  });
}

export function publishProctorConfigDraft(id: string, metadata: ProctorConfigurationMetadata) {
  const uri = buildBaseURI().addQueryParam('action', 'publish').addQueryParam('id', id);
  const data = metadata;

  return Q(ProctorAPI.post(uri.toString(), { data })).then((response) => {
    return response.elements || response;
  });
}
